import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Divider } from "antd";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import { EPolicyActivationError } from "@base/enums";
import { getErrorMsg } from "@base/helpers/common";
import useTurnstile from "@base/hooks/useTurnstile";
import { baseRoutes } from "@base/routes/baseRoutes";
import { responseUI } from "@base/themes/response";
import { IPolicyLookupResult, IPolicyRecord } from "@base/modules/policyActivation/policyActivation.interface";
import policyActivationService from "@base/modules/policyActivation/policyActivationService";

/** The OTP lives for 2 minutes and asking again inside that window silently sends no new SMS. */
const RESEND_LOCK_SECONDS = 120;
const PHONE_PATTERN = /^0\d{9}$/;

type IStep = "lookup" | "confirm" | "done";

export default function PolicyActivation() {
  //! state
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { containerRef, token: turnstileToken, isConfigured: isTurnstileConfigured, resetWidget } = useTurnstile();
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const [step, setStep] = useState<IStep>("lookup");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [policyNo, setPolicyNo] = useState<string>("");
  const [nationalId, setNationalId] = useState<string>("");
  const [patientName, setPatientName] = useState<string>("");

  const [lookupResult, setLookupResult] = useState<IPolicyLookupResult>();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [patientDob, setPatientDob] = useState<string>("");
  const [patientAddress, setPatientAddress] = useState<string>("");
  const [patientEmail, setPatientEmail] = useState<string>("");

  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [resendCounter, setResendCounter] = useState<number>(0);
  const [activatedPolicy, setActivatedPolicy] = useState<IPolicyRecord>();

  //! function
  const startResendCountdown = useCallback(() => {
    clearInterval(intervalRef.current);
    setResendCounter(RESEND_LOCK_SECONDS);

    intervalRef.current = setInterval(() => {
      setResendCounter((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  /** Error codes are UPPER_SNAKE and must never reach the patient as-is. */
  const showError = (error: unknown) => {
    const code = String(getErrorMsg(error));
    const known = Object.values(EPolicyActivationError).includes(code as EPolicyActivationError);

    setErrorMessage(known ? t(`policyActivation.errors.${code}`) : t("policyActivation.errors.UNKNOWN"));

    if (code === EPolicyActivationError.CLAIM_TOKEN_INVALID) {
      // The 10 minute session is gone, so there is nothing left to retry on this screen.
      setStep("lookup");
      setIsOtpSent(false);
      setLookupResult(undefined);
    }
  };

  const handleLookup = async () => {
    setErrorMessage("");

    if (!policyNo.trim() || !nationalId.trim() || !patientName.trim()) {
      setErrorMessage(t("policyActivation.requiredLookup"));
      return;
    }

    try {
      setIsSubmitting(true);
      const resp = await policyActivationService.lookup(
        { policy_no: policyNo.trim(), national_id: nationalId.trim(), patient_name: patientName.trim() },
        turnstileToken ?? undefined,
      );

      setLookupResult(resp.data);
      setStep("confirm");
    } catch (error) {
      showError(error);
    } finally {
      // Turnstile tokens are single use, so the widget is rebuilt after every attempt.
      resetWidget();
      setIsSubmitting(false);
    }
  };

  const handleRequestOtp = async () => {
    setErrorMessage("");

    if (!PHONE_PATTERN.test(phoneNumber)) {
      setErrorMessage(t("policyActivation.errors.PHONE_REQUIRED"));
      return;
    }

    try {
      setIsSubmitting(true);
      await policyActivationService.requestOTP(
        {
          claim_token: String(lookupResult?.claim_token),
          phone_number: phoneNumber,
          lang: i18n.resolvedLanguage,
          // Blank means "keep what the insurer sent", so empty fields are simply not sent.
          patient_dob: patientDob.trim() || undefined,
          patient_address: patientAddress.trim() || undefined,
          patient_email: patientEmail.trim() || undefined,
        },
        turnstileToken ?? undefined,
      );

      setIsOtpSent(true);
      startResendCountdown();
    } catch (error) {
      showError(error);
    } finally {
      resetWidget();
      setIsSubmitting(false);
    }
  };

  const handleActivate = async () => {
    setErrorMessage("");

    if (otp.length !== 6) {
      setErrorMessage(t("policyActivation.errors.OTP_INVALID"));
      return;
    }

    try {
      setIsSubmitting(true);
      const resp = await policyActivationService.activate({ claim_token: String(lookupResult?.claim_token), otp: Number(otp) });

      setActivatedPolicy(resp.data);
      setStep("done");
    } catch (error) {
      // A wrong code writes nothing, so the patient simply tries again.
      setOtp("");
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCountdown = () => {
    const minutes = Math.floor(resendCounter / 60);
    const seconds = resendCounter % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  //! render
  const renderCaptcha = () => {
    if (!isTurnstileConfigured) {
      return null;
    }

    return <div ref={containerRef} className="w-full max-w-full min-w-[300px] min-h-[65px]" />;
  };

  const renderError = () => {
    return errorMessage ? (
      <div className="w-full bg-common-red100 border border-common-red500 p-3 rounded-lg">
        <CommonStyles.TypographyUI use="paragraph" className="text-common-red500 text-sm font-normal font-['Inter'] !mb-0">
          {errorMessage}
        </CommonStyles.TypographyUI>
      </div>
    ) : null;
  };

  const renderLookup = () => {
    return (
      <div className="flex flex-col gap-4">
        <CommonStyles.InputUI
          variant="filled"
          required
          label={t("policyActivation.policyNo")}
          value={policyNo}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPolicyNo(event.target.value)}
        />

        <CommonStyles.InputUI
          variant="filled"
          required
          label={t("policyActivation.nationalId")}
          value={nationalId}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNationalId(event.target.value)}
        />

        <div>
          <CommonStyles.InputUI
            variant="filled"
            required
            label={t("policyActivation.patientName")}
            value={patientName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPatientName(event.target.value)}
          />
          <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-xs font-normal font-['Inter'] !mb-0 mt-1">
            {t("policyActivation.patientNameHint")}
          </CommonStyles.TypographyUI>
        </div>

        {renderCaptcha()}
        {renderError()}

        <CommonStyles.ButtonUI
          type="primary"
          loading={isSubmitting}
          disabled={isTurnstileConfigured && !turnstileToken}
          onClick={handleLookup}
        >
          {t("policyActivation.btnLookup")}
        </CommonStyles.ButtonUI>
      </div>
    );
  };

  const renderConfirm = () => {
    return (
      <div className="flex flex-col gap-4">
        {/* Step 1 only returns a masked summary - enough to recognise the policy, no more. */}
        <div className="w-full bg-common-background rounded-lg p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <InfoItem label={t("policyActivation.patientName")} value={lookupResult?.patient_name_masked} />
          <InfoItem label={t("policyActivation.birthYear")} value={lookupResult?.birth_year} />
          <InfoItem label={t("policyActivation.packageType")} value={lookupResult?.package_type} />
        </div>

        <CommonStyles.InputUI
          variant="filled"
          required
          disabled={isOtpSent}
          label={t("policyActivation.phoneNumber")}
          placeholder="0912345678"
          value={phoneNumber}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(event.target.value.trim())}
        />

        <CommonStyles.InputUI
          variant="filled"
          disabled={isOtpSent}
          label={t("policyActivation.patientDob")}
          placeholder="1990-04-15"
          value={patientDob}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPatientDob(event.target.value)}
        />

        <CommonStyles.InputUI
          variant="filled"
          disabled={isOtpSent}
          label={t("Address")}
          value={patientAddress}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPatientAddress(event.target.value)}
        />

        <CommonStyles.InputUI
          variant="filled"
          disabled={isOtpSent}
          label={t("Email")}
          value={patientEmail}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPatientEmail(event.target.value)}
        />

        <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-xs font-normal font-['Inter'] !mb-0">
          {t("policyActivation.optionalHint")}
        </CommonStyles.TypographyUI>

        {!isOtpSent ? renderCaptcha() : null}
        {renderError()}

        {!isOtpSent ? (
          <CommonStyles.ButtonUI
            type="primary"
            loading={isSubmitting}
            disabled={isTurnstileConfigured && !turnstileToken}
            onClick={handleRequestOtp}
          >
            {t("policyActivation.btnRequestOtp")}
          </CommonStyles.ButtonUI>
        ) : (
          <div className="flex flex-col gap-3 items-center">
            <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-sm font-normal font-['Inter'] !mb-0">
              {t("policyActivation.otpSent", { phone: phoneNumber })}
            </CommonStyles.TypographyUI>

            <CommonStyles.InputUI use="otp" length={6} value={otp} otpOnChange={setOtp} />

            {resendCounter > 0 ? (
              <CommonStyles.TypographyUI className="text-common-subtext">
                {t("policyActivation.resendIn", { time: formatCountdown() })}
              </CommonStyles.TypographyUI>
            ) : (
              <CommonStyles.TypographyUI
                className="font-semibold text-common-primary cursor-pointer"
                onClick={() => {
                  setIsOtpSent(false);
                  setOtp("");
                }}
              >
                {t("LoginPage.Resend code")}
              </CommonStyles.TypographyUI>
            )}

            <CommonStyles.ButtonUI type="primary" loading={isSubmitting} onClick={handleActivate}>
              {t("policyActivation.btnActivate")}
            </CommonStyles.ButtonUI>
          </div>
        )}
      </div>
    );
  };

  const renderDone = () => {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <CommonIcons.IconSuccess />

        <CommonStyles.TypographyUI use="title" level={4} className="!mb-0">
          {t("policyActivation.doneTitle")}
        </CommonStyles.TypographyUI>

        {/* `process_status` comes back as NEW: queued, not finished - so no link to the packages. */}
        <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-sm font-normal font-['Inter']">
          {t("policyActivation.doneDescription")}
        </CommonStyles.TypographyUI>

        <div className="w-full max-w-md bg-common-background rounded-lg p-3 flex flex-col gap-1 text-left">
          <InfoItem label={t("policyActivation.policyNo")} value={activatedPolicy?.policy_no} />
          <InfoItem label={t("policyActivation.patientName")} value={activatedPolicy?.patient_name} />
          <InfoItem label={t("policyActivation.phoneNumber")} value={activatedPolicy?.patient_phone_number} />
          <InfoItem label={t("policyActivation.packageType")} value={activatedPolicy?.patient_package_type} />
        </div>

        <CommonStyles.ButtonUI type="primary" onClick={() => navigate(baseRoutes.login)}>
          {t("Login")}
        </CommonStyles.ButtonUI>
      </div>
    );
  };

  return (
    <div className={`${responseUI.screen} ${responseUI.padding} py-10`}>
      <div className="max-w-2xl mx-auto bg-common-white rounded-2xl p-6 flex flex-col gap-4">
        <div className="text-center">
          <CommonStyles.TypographyUI use="title" level={3} className={`${responseUI.text.title}`}>
            {t("policyActivation.title")}
          </CommonStyles.TypographyUI>
          <CommonStyles.TypographyUI use="paragraph" className={`${responseUI.text.subTitle}`}>
            {t("policyActivation.subTitle")}
          </CommonStyles.TypographyUI>
        </div>

        <CommonStyles.StepUI
          current={step === "lookup" ? 0 : step === "confirm" ? 1 : 2}
          items={[{ title: t("policyActivation.stepLookup") }, { title: t("policyActivation.stepConfirm") }, { title: t("policyActivation.stepDone") }]}
        />

        <Divider className="my-0" />

        {step === "lookup" ? renderLookup() : null}
        {step === "confirm" ? renderConfirm() : null}
        {step === "done" ? renderDone() : null}
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value?: string | number }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-common-subtext text-xs font-normal font-['Inter']">{label}:</span>
      <span className="text-common-maintext text-sm font-medium font-['Inter']">{value ?? "-"}</span>
    </div>
  );
}
